import React, { useState } from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useUserPrescreenedPositions } from 'queries/user'

const DEFAULT_PAGE_SIZE = 10

type UserPrescreenedPositionsContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useUserPrescreenedPositions>['data']>
    isFetching: boolean
    pageSize: number
    canGoPrevious: boolean
    canGoNext: boolean
  }
  actions: {
    goToPreviousPage: () => void
    goToNextPage: () => void
  }
}

const [Context, useUserPrescreenedPositionsContextValue] =
  createContext<UserPrescreenedPositionsContextState>({
    name: 'UserPrescreenedPositionsContext',
  })

type UserPrescreenedPositionsProviderProps = {
  userId: number
  children: React.ReactNode
}

export const UserPrescreenedPositionsProvider = ({
  userId,
  children,
}: UserPrescreenedPositionsProviderProps) => {
  // Always fetch with direction=next; keep a stack of page-start cursors so
  // Previous/Next replace the page instead of appending.
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([
    undefined,
  ])
  const [pageIndex, setPageIndex] = useState(0)
  const cursor = pageCursors[pageIndex]

  const { data, isPending, isError, error, isFetching } =
    useUserPrescreenedPositions({
      userId,
      cursor,
      direction: 'next',
      pageSize: DEFAULT_PAGE_SIZE,
    })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error">
        {parseErrorMessage(error) ||
          'Failed to load prescreened positions. Please try again.'}
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: UserPrescreenedPositionsContextState = {
    state: {
      data,
      isFetching,
      pageSize: DEFAULT_PAGE_SIZE,
      canGoPrevious: pageIndex > 0,
      canGoNext: data.nextCursor !== null,
    },
    actions: {
      goToPreviousPage: () => {
        if (pageIndex === 0) {
          return
        }
        setPageIndex(pageIndex - 1)
      },
      goToNextPage: () => {
        if (data.nextCursor === null) {
          return
        }
        const nextPageIndex = pageIndex + 1
        setPageCursors((current) => {
          const next = current.slice(0, nextPageIndex)
          next[nextPageIndex] = data.nextCursor ?? undefined
          return next
        })
        setPageIndex(nextPageIndex)
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useUserPrescreenedPositionsContextValue }
